/** The Audit screen's Target column. */
import type { AuditEntry } from '../../api/v1';
import { isHighPriority } from '../../api/task-priority';
import { dispatchedTaskTitle } from './audit-action-label';

/** An audit row's outcome in words, as the Audit screen's outcome filter
 *  names them; My activity reads the same (review BC-F11, 2026-10-31). */
export const AUDIT_OUTCOME_LABEL: Readonly<Record<AuditEntry['outcome'], string>> = {
  allowed: 'Allowed',
  denied: 'Denied',
  published: 'Published',
  accepted: 'Accepted',
  rejected: 'Rejected',
  timed_out: 'Timed out',
  outcome_unknown: 'Unknown',
};

/** How many items of a list the Target cell shows before "+N more". */
const TARGET_LIST_CAP = 3;

/**
 * The Target column: `key=value` for each plain value. A nested object
 * (a site-settings row's `before`/`after`) is left out -- the row's
 * sentence says "from X to Y" instead, and `String({})` would only print
 * "[object Object]" -- and a list is capped, so a whole-floor zone change
 * does not dump every zone name into one cell (#28).
 */
export function summarizeTarget(target: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(target)) {
    if (Array.isArray(v)) {
      const items = v.filter((x) => x === null || typeof x !== 'object').map(String);
      if (items.length === 0) continue;
      const shown = items.slice(0, TARGET_LIST_CAP).join(', ');
      const more = items.length - TARGET_LIST_CAP;
      parts.push(`${k}=${shown}${more > 0 ? ` +${more} more` : ''}`);
    } else if (v !== null && typeof v === 'object') {
      continue;
    } else {
      parts.push(`${k}=${String(v)}`);
    }
  }
  return parts.length ? parts.join(', ') : '—';
}

/**
 * The Target cell for a row (2026-10-31). A schedule or a blocked time
 * is named -- the schedule's name, or the reason a time was blocked --
 * because the Action column's sentence already says what changed, from
 * what to what, and the raw target behind it is rule text, epoch
 * numbers and schedule ids that mean nothing to a reader. "—" when the
 * row names none. A command to a robot, door or lift is named too
 * (`commandTargetText`). Every other action keeps its `key=value`
 * summary; the CSV export keeps the raw target for every row.
 */
export function auditTargetText(entry: {
  action: string;
  target: Record<string, unknown> | null | undefined;
}): string {
  const target = entry.target ?? {};
  if (entry.action.startsWith('schedule.') || entry.action.startsWith('maintenance.')) {
    const name = [target.name, target.reason, target.to_reason, target.from_reason].find(
      (v): v is string => typeof v === 'string' && v.trim() !== '',
    );
    return name ?? '—';
  }
  return commandTargetText(entry.action, target) ?? summarizeTarget(target);
}

function named(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

/**
 * A command to a robot, a door or a lift by the name of what it was aimed
 * at (2026-10-31), as the Action sentence names it: the robot, the door,
 * the lift, "Lifts" for the site-wide lift switch, a new task by what it
 * does and where. A task control names the task's id, the one handle a
 * row carries for it. Null for any other action, and for a row that does
 * not carry the name, which keeps its `key=value` summary.
 */
function commandTargetText(action: string, target: Record<string, unknown>): string | null {
  switch (action) {
    case 'task.cancel':
    case 'task.kill':
    case 'task.resume':
    case 'task.interrupt': {
      const id = named(target.task_id);
      return id ? `Task ${id}` : null;
    }
    case 'task.dispatch':
      return dispatchTargetText(target);
    case 'robot.commission':
    case 'robot.decommission':
    case 'robot.set_idle_behavior':
    case 'robot.fire_relocalize':
      return named(target.robot);
    case 'lift.arm_writes':
      return 'Lifts';
    case 'door.arm_writes':
      return 'Doors';
    case 'lift.rule_hold':
    case 'lift.rule_release':
    case 'lift.rule_hold_ended':
    case 'lift.rule_breaker_reset':
    case 'lift.rule_window_create':
    case 'lift.rule_window_update':
    case 'lift.rule_window_delete':
    case 'lift.manual_call':
    case 'lift.manual_close':
    case 'lift.recover':
    case 'lift.end_session':
    case 'lift.advance':
    case 'lift.abort':
      return named(target.lift);
    case 'door.open':
    case 'door.close':
    case 'door.declare_open':
    case 'door.revoke_override':
      return named(target.door);
    default:
      return null;
  }
}

/**
 * A new task's Target cell: what it does and where, as the calendar titles
 * a run ("Vacuum · L4-Lobby"), and "High priority" first when it was sent
 * High (api-server 0.6.56). A dispatch's target is its whole task,
 * priority included: printed as "priority=0" on every row, it was noise
 * (2026-10-31). Only High is said, in words; an old 1-5 value is Normal,
 * as it was sent. A refused High keeps its words: the Outcome says
 * "denied". A row with no category keeps its `key=value` summary, less
 * the priority and the fields the task left empty (`robot=null`,
 * `earliest_start_ms=null`, on every row the same way); the CSV keeps
 * them all.
 */
function dispatchTargetText(target: Record<string, unknown>): string {
  const { priority, ...rest } = target;
  const what =
    dispatchedTaskTitle(target) ??
    summarizeTarget(
      Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== null && v !== undefined)),
    );
  if (!isHighPriority(priority)) return what;
  return what === '—' ? 'High priority' : `High priority, ${what}`;
}
